import { createContext, useContext, useState, type ReactNode } from 'react'
import { adminApi } from '@/lib/api/admin'

interface AdminContextType {
  adminKey: string | null
  isAuthenticated: boolean
  login: (key: string) => Promise<void>
  logout: () => void
}

const AdminContext = createContext<AdminContextType | undefined>(undefined)

const STORAGE_KEY = 'etx_admin_key'

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [adminKey, setAdminKey] = useState<string | null>(
    () => sessionStorage.getItem(STORAGE_KEY),
  )

  const login = async (key: string) => {
    const valid = await adminApi.verifyKey(key)
    if (!valid) throw new Error('Invalid admin key')
    sessionStorage.setItem(STORAGE_KEY, key)
    setAdminKey(key)
  }

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY)
    setAdminKey(null)
  }

  return (
    <AdminContext.Provider value={{ adminKey, isAuthenticated: adminKey !== null, login, logout }}>
      {children}
    </AdminContext.Provider>
  )
}

export function useAdminAuth() {
  const context = useContext(AdminContext)
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}
